import { createFileRoute, useNavigate, Link } from "@tanstack/react-router";
import { useState, useMemo } from "react";
import { useMutation, useQueryClient, useQuery } from "@tanstack/react-query";
import { ArrowLeft, Link2 } from "lucide-react";
import { toast } from "sonner";

import { Header } from "@/components/layout/Header";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { TemplateEditor } from "@/components/proposals";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { getSimulator } from "@/lib/api/simulators";
import { createProposalTemplate, listProposalTemplates } from "@/lib/api/proposal-templates";
import { listAllOutputFieldGroups } from "@/lib/api/output-fields";
import { listAllFormFieldGroups } from "@/lib/api/form-fields";
import { getErrorMessage } from "@/lib/api/client";
import { AdminGuard } from "@/lib/auth/AdminGuard";
import type { TemplatePage, CanvasData, OutputField, OutputFieldGroupWithFields, FormField, FormFieldGroupWithFields } from "@/types";

export const Route = createFileRoute(
  "/_dashboard/admin/simulators/$id/templates/new"
)({
  component: NewTemplatePage,
  validateSearch: (search: Record<string, unknown>) => ({
    version: search.version ? Number(search.version) : undefined,
  }),
});

function NewTemplatePage() {
  const { id } = Route.useParams();
  const { version: versionId } = Route.useSearch();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const simulatorId = Number(id);

  const [step, setStep] = useState<"info" | "editor">("info");
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [parentTemplateId, setParentTemplateId] = useState<number | null>(null);
  const [pages, setPages] = useState<TemplatePage[]>([]);

  // Get simulator info for the header
  const { data: simulator } = useQuery({
    queryKey: ["simulator", simulatorId],
    queryFn: () => getSimulator(simulatorId),
  });

  // Use the versionId from URL, or fall back to computing it from simulator data
  const activeVersionId = useMemo(() => {
    // Use URL version if available
    if (versionId) return versionId;
    // Fall back to computing from simulator data
    if (!simulator?.versions?.length) return null;
    // Prefer published version, fall back to latest
    const publishedVersion = simulator.versions.find(v => v.status === "published");
    if (publishedVersion) return publishedVersion.id;
    // Sort by version_number descending and take the first
    const sortedVersions = [...simulator.versions].sort((a, b) => b.version_number - a.version_number);
    return sortedVersions[0]?.id ?? null;
  }, [versionId, simulator?.versions]);

  // Fetch potential parent templates (templates without a parent)
  const { data: potentialParents } = useQuery({
    queryKey: ["proposal-templates", activeVersionId, "parents-only"],
    queryFn: () => listProposalTemplates(activeVersionId!, { active_only: false, parents_only: true }),
    enabled: !!activeVersionId,
  });

  // Fetch output field groups for the simulator's version
  const { data: outputFieldGroups } = useQuery({
    queryKey: ["output-field-groups", activeVersionId],
    queryFn: () => listAllOutputFieldGroups(activeVersionId!),
    enabled: !!activeVersionId,
  });

  // Fetch form field groups for the simulator's version
  const { data: formFieldGroups } = useQuery({
    queryKey: ["form-field-groups", activeVersionId],
    queryFn: () => listAllFormFieldGroups(activeVersionId!),
    enabled: !!activeVersionId,
  });

  // Flatten output fields from groups
  const flattenOutputFields = (group: OutputFieldGroupWithFields): OutputField[] => {
    const fields = [...group.fields];
    for (const child of group.children) {
      fields.push(...flattenOutputFields(child));
    }
    return fields;
  };

  // Flatten form fields from groups
  const flattenFormFields = (group: FormFieldGroupWithFields): FormField[] => {
    const fields = [...group.fields];
    for (const child of group.children) {
      fields.push(...flattenFormFields(child));
    }
    return fields;
  };

  // Get all output fields for the template editor
  const allOutputFields = useMemo(() => {
    if (!outputFieldGroups) return [];
    const fields: OutputField[] = [];
    for (const group of outputFieldGroups) {
      fields.push(...flattenOutputFields(group));
    }
    return fields;
  }, [outputFieldGroups]);

  // Get all form fields for the template editor
  const allFormFields = useMemo(() => {
    if (!formFieldGroups) return [];
    const fields: FormField[] = [];
    for (const group of formFieldGroups) {
      fields.push(...flattenFormFields(group));
    }
    return fields;
  }, [formFieldGroups]);

  // Compute inherited pages from selected parent template
  const parentPages = useMemo(() => {
    if (!parentTemplateId || !potentialParents?.items) return [];
    const parent = potentialParents.items.find(t => t.id === parentTemplateId);
    if (!parent?.pages_data) return [];
    return parent.pages_data.map((page: TemplatePage, idx: number) => ({
      ...page,
      index: idx,
      is_inherited: true,
    }));
  }, [parentTemplateId, potentialParents?.items]);

  // Create template mutation - now uses versionId instead of simulatorId
  const createMutation = useMutation({
    mutationFn: (data: { pages: TemplatePage[]; inheritedPageAdditions?: Record<string, CanvasData> | null }) => {
      if (!activeVersionId) {
        throw new Error("No version selected");
      }
      return createProposalTemplate(activeVersionId, {
        name,
        description: description || undefined,
        pages_data: data.pages,
        inherited_page_additions: data.inheritedPageAdditions,
        parent_template_id: parentTemplateId,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["proposal-templates", activeVersionId],
      });
      toast.success("Template cree avec succes");
      navigate({
        to: "/admin/simulators/$id",
        params: { id },
        search: { tab: "templates", version: activeVersionId || undefined },
      });
    },
    onError: (error) => {
      toast.error("Erreur lors de la creation", {
        description: getErrorMessage(error),
      });
    },
  });

  const handleNext = () => {
    if (!name.trim()) {
      toast.error("Le nom du template est requis");
      return;
    }
    setStep("editor");
  };

  const handleSave = (updatedPages: TemplatePage[], inheritedPageAdditions?: Record<string, CanvasData> | null) => {
    setPages(updatedPages);
    createMutation.mutate({ pages: updatedPages, inheritedPageAdditions });
  };

  return (
    <AdminGuard>
    <div className="flex flex-col h-full">
      <Header
        title={step === "info" ? "Nouveau template" : name}
        subtitle={simulator?.name}
      >
        <Button variant="ghost" asChild>
          <Link
            to="/admin/simulators/$id"
            params={{ id }}
            search={{ tab: "templates", version: activeVersionId || undefined }}
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Retour
          </Link>
        </Button>
      </Header>

      <div className="flex-1 overflow-auto">
        {step === "info" ? (
          <div className="flex items-center justify-center min-h-full p-8">
            <Card className="w-full max-w-2xl">
              <CardHeader>
                <CardTitle>Informations du template</CardTitle>
                <CardDescription>
                  Definissez le nom et la description de votre template de
                  proposition commerciale.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="space-y-2">
                  <Label htmlFor="name">Nom du template *</Label>
                  <Input
                    id="name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Proposition commerciale standard"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="description">Description (optionnel)</Label>
                  <Textarea
                    id="description"
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    placeholder="Description du template..."
                    rows={4}
                  />
                </div>
                
                {/* Parent template selector */}
                <div className="space-y-2">
                  <Label htmlFor="parent" className="flex items-center gap-2">
                    <Link2 className="h-4 w-4" />
                    Template parent (optionnel)
                  </Label>
                  <Select
                    value={parentTemplateId?.toString() ?? "none"}
                    onValueChange={(value) => setParentTemplateId(value === "none" ? null : Number(value))}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Aucun (template independant)" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Aucun (template independant)</SelectItem>
                      {potentialParents?.items.map((template) => (
                        <SelectItem key={template.id} value={template.id.toString()}>
                          {template.name}
                          {template.pages_data?.length > 0 && ` (${template.pages_data.length} pages)`}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-sm text-muted-foreground">
                    Si vous choisissez un template parent, ce nouveau template heritera 
                    automatiquement de toutes ses pages et pourra ajouter ses propres pages supplementaires.
                  </p>
                </div>

                <div className="flex justify-end pt-2">
                  <Button onClick={handleNext}>Continuer vers l&apos;editeur</Button>
                </div>
              </CardContent>
            </Card>
          </div>
        ) : (
          <TemplateEditor
            initialPages={parentPages.length > 0 ? parentPages : undefined}
            ownPagesStartIndex={parentPages.length}
            onSave={handleSave}
            isSaving={createMutation.isPending}
            outputFields={allOutputFields}
            formFields={allFormFields}
          />
        )}
      </div>
    </div>
    </AdminGuard>
  );
}
